import React, { useEffect, useState } from "react";
import axios from "axios";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import toast from "react-hot-toast";
import Loader from "../../assets/Loader.gif";

const BASE_URL = "https://admin.chatindiacom.cloud/api";

function GroupLevelList() {
  const [levels, setLevels] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [activeTab, setActiveTab] = useState("levels"); // "levels" | "rooms"

  const [form, setForm] = useState({
    level_number: "",
    title: "",
    coins_required: "",
    icon: null,
  });

  const getToken = () => localStorage.getItem("token");

  const api = axios.create({
    baseURL: BASE_URL,
    headers: { Authorization: `Bearer ${getToken()}` },
  });

  const fetchLevels = async () => {
    setLoading(true);
    try {
      const res = await api.get("/group-levels/list");
      setLevels(res.data.levels || []);
    } catch (err) {
      toast.error("Failed to load group levels");
    }
    setLoading(false);
  };

  const fetchRooms = async () => {
    setRoomsLoading(true);
    try {
      const res = await api.get("/group-levels/all-rooms");
      setRooms(res.data.rooms || []);
    } catch (err) {
      toast.error("Failed to load rooms");
    }
    setRoomsLoading(false);
  };

  useEffect(() => {
    fetchLevels();
    fetchRooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAddModal = () => {
    setEditingLevel(null);
    setForm({ level_number: "", title: "", coins_required: "", icon: null });
    setOpen(true);
  };

  const openEditModal = (lvl) => {
    setEditingLevel(lvl);
    setForm({
      level_number: lvl.level_number,
      title: lvl.title,
      coins_required: lvl.coins_required,
      icon: null,
    });
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSubmit = async () => {
    if (!form.level_number || !form.title || !form.coins_required) {
      toast.error("Please fill all fields");
      return;
    }

    const fd = new FormData();
    fd.append("level_number", form.level_number);
    fd.append("title", form.title);
    fd.append("coins_required", form.coins_required);
    if (form.icon) fd.append("icon", form.icon);

    setSaving(true);
    try {
      if (editingLevel) {
        fd.append("id", editingLevel.id);
        await api.post("/group-levels/edit", fd);
        toast.success("Group level updated successfully");
      } else {
        await api.post("/group-levels/create", fd);
        toast.success("Group level added successfully");
      }
      handleClose();
      fetchLevels();
      fetchRooms(); // levels change hone se room ka calculated level bhi badal sakta hai
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this level?")) return;
    try {
      await api.post("/group-levels/delete", { id });
      toast.success("Level deleted");
      fetchLevels();
      fetchRooms();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen px-4 py-6 dark:bg-primary md:px-8 xl:ml-72">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold font-poppins text-[#000000] dark:text-darkText">
          Group (Room) Levels
        </h2>
        {activeTab === "levels" && (
          <button
            onClick={openAddModal}
            style={{ backgroundColor: "#6c5ce7" }}
            className="px-4 py-2 text-sm font-semibold text-white rounded-lg font-poppins shadow-sm hover:opacity-90"
          >
            + Add Level
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab("levels")}
          className={`px-4 py-2 text-sm rounded-lg font-poppins ${
            activeTab === "levels"
              ? "bg-[#6c5ce7] text-white"
              : "bg-[#F2F2F2] dark:bg-[#1F1F1F] text-[#5B5B5B] dark:text-[#AAAAAA]"
          }`}
        >
          Levels Setup
        </button>
        <button
          onClick={() => setActiveTab("rooms")}
          className={`px-4 py-2 text-sm rounded-lg font-poppins ${
            activeTab === "rooms"
              ? "bg-[#6c5ce7] text-white"
              : "bg-[#F2F2F2] dark:bg-[#1F1F1F] text-[#5B5B5B] dark:text-[#AAAAAA]"
          }`}
        >
          Rooms &amp; Their Level
        </button>
      </div>

      {/* Tab 1: Level Setup Table */}
      {activeTab === "levels" && (
        <div className="overflow-x-auto bg-white border border-[#F2F2F2] rounded-xl dark:bg-primary dark:border-[#1F1F1F]">
          <table className="w-full text-sm text-left font-poppins">
            <thead>
              <tr className="text-[#000000] dark:text-darkText border-b border-[#F2F2F2] dark:border-[#1F1F1F]">
                <th className="px-4 py-3">Icon</th>
                <th className="px-4 py-3">Level No.</th>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Coins Required</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-[#5B5B5B] dark:text-[#AAAAAA]">
                    Loading...
                  </td>
                </tr>
              ) : levels.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-[#5B5B5B] dark:text-[#AAAAAA]">
                    No group levels found. Click "Add Level" to create one.
                  </td>
                </tr>
              ) : (
                levels.map((lvl) => (
                  <tr
                    key={lvl.id}
                    className="border-b border-[#F2F2F2] dark:border-[#1F1F1F] text-[#5B5B5B] dark:text-[#AAAAAA]"
                  >
                    <td className="px-4 py-3">
                      {lvl.icon ? (
                        <img src={lvl.icon} alt="icon" className="object-cover w-9 h-9 rounded-md" />
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-3">{lvl.level_number}</td>
                    <td className="px-4 py-3">{lvl.title}</td>
                    <td className="px-4 py-3">{lvl.coins_required}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => openEditModal(lvl)}
                        className="px-3 py-1 mr-2 text-xs bg-[#F2F2F2] dark:bg-[#1F1F1F] dark:text-darkText rounded-md"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(lvl.id)}
                        className="px-3 py-1 text-xs text-white bg-red-500 rounded-md"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Tab 2: Rooms with calculated level */}
      {activeTab === "rooms" && (
        <div className="overflow-x-auto bg-white border border-[#F2F2F2] rounded-xl dark:bg-primary dark:border-[#1F1F1F]">
          <table className="w-full text-sm text-left font-poppins">
            <thead>
              <tr className="text-[#000000] dark:text-darkText border-b border-[#F2F2F2] dark:border-[#1F1F1F]">
                <th className="px-4 py-3">Room</th>
                <th className="px-4 py-3">Host</th>
                <th className="px-4 py-3">Total Coins Shared</th>
                <th className="px-4 py-3">Current Level</th>
              </tr>
            </thead>
            <tbody>
              {roomsLoading ? (
                <tr>
                  <td colSpan={4} className="py-6 text-center text-[#5B5B5B] dark:text-[#AAAAAA]">
                    Loading...
                  </td>
                </tr>
              ) : rooms.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-6 text-center text-[#5B5B5B] dark:text-[#AAAAAA]">
                    No rooms found.
                  </td>
                </tr>
              ) : (
                rooms.map((room) => (
                  <tr
                    key={room.room_id}
                    className="border-b border-[#F2F2F2] dark:border-[#1F1F1F] text-[#5B5B5B] dark:text-[#AAAAAA]"
                  >
                    <td className="px-4 py-3">{room.room_name}</td>
                    <td className="px-4 py-3">{room.host_name}</td>
                    <td className="px-4 py-3">{room.total_coins}</td>
                    <td className="px-4 py-3">
                      {room.current_level ? (
                        <span className="inline-flex items-center gap-2">
                          {room.current_level.icon && (
                            <img
                              src={room.current_level.icon}
                              alt="level"
                              className="w-6 h-6 rounded-full"
                            />
                          )}
                          {room.current_level.title}
                        </span>
                      ) : (
                        <span className="text-xs">No level yet</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit Modal */}
      <Dialog open={open} onClose={handleClose} fullWidth className="custom-dialog">
        <DialogContent className="flex flex-col gap-4 dark:bg-primary">
          <h2 className="text-xl text-center font-poppins text-[#000000] dark:text-darkText">
            {editingLevel ? "Edit Group Level" : "Add Group Level"}
          </h2>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-poppins text-[#5B5B5B] dark:text-darkText">
              Level Number
            </label>
            <input
              type="number"
              value={form.level_number}
              onChange={(e) => setForm({ ...form, level_number: e.target.value })}
              className="border border-gray-400 rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-1 focus:ring-gray-600 dark:bg-primary dark:text-darkText"
              placeholder="e.g. 1"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-poppins text-[#5B5B5B] dark:text-darkText">
              Title
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="border border-gray-400 rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-1 focus:ring-gray-600 dark:bg-primary dark:text-darkText"
              placeholder="e.g. Gold Room"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-poppins text-[#5B5B5B] dark:text-darkText">
              Coins Required (total room coins)
            </label>
            <input
              type="number"
              value={form.coins_required}
              onChange={(e) => setForm({ ...form, coins_required: e.target.value })}
              className="border border-gray-400 rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-1 focus:ring-gray-600 dark:bg-primary dark:text-darkText"
              placeholder="e.g. 50000"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-poppins text-[#5B5B5B] dark:text-darkText">
              Icon (optional)
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setForm({ ...form, icon: e.target.files[0] })}
              className="text-sm font-poppins text-[#5B5B5B] dark:text-darkText"
            />
          </div>
        </DialogContent>

        <DialogContent className="flex justify-center gap-4 pb-4 dark:bg-primary">
          <button
            className="w-[150px] py-2 border border-header text-[#3A3333] dark:text-darkText rounded-lg"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            style={{ backgroundColor: "#0D9947" }}
            className="w-[150px] py-2 text-white rounded-lg"
          >
            {saving ? (
              <div className="flex justify-center py-1">
                <img src={Loader} alt="loader" height={10} width={10} />
              </div>
            ) : (
              "Save"
            )}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default GroupLevelList;